import * as React from 'react';
import Downshift, {
  GetItemPropsOptions,
  DownshiftProps,
  PropGetters
} from 'downshift';

// Styles
import {
  Chevron,
  DropdownMenuButton,
  Item,
  Menu,
  StyledDropdownProps,
  ButtonText,
  TextWrapper,
  IconWrapper
} from './Dropdown.style';

// Components
import Grid from '../../layoutComponents/Grid';

export interface IDropdownProps
  extends StyledDropdownProps,
    Omit<React.HTMLAttributes<HTMLMenuElement & HTMLButtonElement>, 'color'> {
  /**
   * Label text for dropdown when a selection
   * has not yet been made.
   */
  buttonLabel: string;

  /**
   * Description of the dropdown to announce to screenreaders.
   */
  'aria-label'?: string;

  /**
   * Where description of dropdown is derived from
   */
  'aria-labelledby'?: string;

  /**
   * Callback fired when the user makes a dropdown selection.
   *
   * @param selectedItem Value of the selected option.
   */
  onChange: (selectedItem: any) => void;

  /**
   * OnBlur callback
   */
  onBlur?: (e: React.FocusEvent<HTMLButtonElement>) => void;

  /**
   * OnFocus callback
   */
  onFocus?: (e: React.FocusEvent<HTMLButtonElement>) => void;

  /**
   * List of dropdown options.
   */
  options: IDropdownOption[];

  /**
   * Dropdown error state
   */
  hasError?: boolean;

  /**
   * Dropdown disabled state
   */
  disabled?: boolean;

  /**
   * Pre-selected value
   */
  defaultOption?: IDropdownOption;

  /**
   * External control of selected value
   */
  selectedOption?: IDropdownOption | null;

  enableSupportPageStyle?: boolean;
}

export interface IDropdownOption {
  /**
   * Display value shown to the user in the dropdown list.
   */
  label: string;

  /**
   * Internal value of the selected dropdown option.
   */
  value: any;

  /**
   * Icon to display next to the option
   */
  icon?: JSX.Element;
}

export interface IDropdownItemProps {
  /**
   * Object that contains label and value properties of dropdown item
   */
  option: IDropdownOption;
  /**
   * Index of dropdown item in selectedItems array
   */
  index: number;
  /**
   * Downshift helper function that spreads current item's props
   */
  getItemProps: (options: GetItemPropsOptions<React.AllHTMLAttributes<HTMLLIElement>>) => any;

  enableSupportPageStyle?: boolean;
}

/**
 * This type is a combination of props that are passed to Downshift
 * It defines
 *  - DownshiftProps => props that are passed to the Downshift render props
 *  - PropGetters => helper functions that fetch props from our components
 *                   and passes them to Downshift.
 */
type DownshiftComboProps = DownshiftProps<
  React.AllHTMLAttributes<HTMLLIElement>
> &
  PropGetters<React.AllHTMLAttributes<HTMLLIElement>>;
/**
 * - List item for our Dropdown menu
 *
 * @param {IDropdownItemProps} {
 *   option,
 *   index,
 *   getItemProps
 * }
 * @returns {React.ReactElement<React.ReactElement>}
 */

const DropdownItem = ({
  option,
  index,
  getItemProps,
  enableSupportPageStyle
}: IDropdownItemProps): React.ReactElement<React.ReactElement> => {
  return (
    <Item
      {...getItemProps({
        key: option.value,
        index,
        item: option,
      })}
      p={2}
      width={1}
      border="none"
      backgroundColor="background.enabled"
      m={0}
      fontSize={0}
      color="form.text.menu.enabled"
      enableSupportPageStyle={enableSupportPageStyle}
    >
      <Grid alignItems="center">
        {option.icon && <IconWrapper>{option.icon}</IconWrapper>}
        <TextWrapper>{option.label}</TextWrapper>
      </Grid>
    </Item>
  );
};

/**
 * Dropdown component managed by Downshift.
 *
 * @see https://github.com/downshift-js/downshift
 */

const Dropdown: React.FC<IDropdownProps> = ({
  buttonLabel,
  options,
  defaultOption,
  selectedOption,
  onChange,
  onFocus,
  onBlur,
  id,
  hasError,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  disabled,
  enableSupportPageStyle,
  ...props
}) => {
  // selectedItem is a Downshift prop that is defined with an any type!
  // const handleChange = (selectedItem: any): void => {
  //   onChange(selectedItem);
  // };

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      <Downshift
        onChange={onChange}
        initialSelectedItem={defaultOption}
        selectedItem={selectedOption}
        itemToString={item => (item ? item.value : '')}
      >
        {({
          isOpen,
          getToggleButtonProps,
          getMenuProps,
          getItemProps,
          selectedItem
        }: DownshiftComboProps): React.ReactElement<
          React.HTMLAttributes<HTMLDivElement>
        > => {
          const aria = ariaLabelledBy
            ? {
                'aria-labelledby': ariaLabelledBy
              }
            : {
                'aria-label': ariaLabel
              };

          const menuProps = {
            boxShadow: 'lg',
            type: 'select',
            isActiveProp: isOpen,
            ...aria
          };

          const buttonProps = getToggleButtonProps({
            id,
            disabled,
            onFocus,
            onBlur,
            ...aria
          });

          const rootProps = {
            style: { width: '100%' },
            ...aria
          };
          
          return (
            <div {...rootProps}>
              <DropdownMenuButton
                {...buttonProps}
                onClick={event =>
                  !event.currentTarget.disabled
                    ? buttonProps.onClick(event)
                    : undefined
                }
                hasError={hasError}
                isActiveProp={isOpen}
                m={0}
                p={2}
                pr={1}
                width={1}
                display="flex"
                fontFamily="primary"
                justifyContent="space-between"
                alignItem="center"
                enableSupportPageStyle={enableSupportPageStyle}
                {...props}
              >
                <ButtonText
                  kind="label"
                  disabled={disabled}
                  isPlaceholder={!selectedItem}
                  marginRight={1}
                  enableSupportPageStyle={enableSupportPageStyle}
                >
                  {selectedItem ? selectedItem.label : buttonLabel}
                </ButtonText>
                <Chevron isActiveProp={isOpen}>
                  <path
                    d="M15.997 13.374l-7.081 7.081L7 18.54l8.997-8.998 9.003 9-1.916 1.916z"
                    transform="scale(1, -1) translate(0, -30)"
                  />
                </Chevron>
              </DropdownMenuButton>
              {isOpen ? (
                <Menu
                  {...getMenuProps(menuProps)}
                  p={0}
                  mt={0}
                  position="absolute"
                  boxShadow="md"
                  width={1}
                  zIndex={4}
                  hasError={hasError}
                  enableSupportPageStyle={enableSupportPageStyle}
                >
                  {options.map((option: IDropdownOption, index: number) => {
                    return (
                      <DropdownItem
                        key={index}
                        index={index}
                        option={option}
                        getItemProps={getItemProps}
                        enableSupportPageStyle={enableSupportPageStyle}
                      />
                    );
                  })}
                </Menu>
              ) : null}
            </div>
          );
        }}
      </Downshift>
    </div>
  );
};

export default Dropdown;
